import { useMutation } from "@apollo/client";
import {
  Alert,
  Container,
  EmptyState,
  Text,
  useModal,
} from "@fluidattacks/design";
import { Fragment, useCallback } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useOrganizationStakeholdersQuery } from "./hooks";
import { MembersTable } from "./members-table";
import { AddMembersModal } from "./members-table/modals/add-modal";
import { ADD_STAKEHOLDER_MUTATION } from "./members-table/org-actions/queries";
import { handleGrantError } from "./members-table/org-actions/utils";
import type { IOrganizationMembers } from "./types";

import { SectionHeader } from "components/section-header";
import type { IStakeholderFormValues } from "features/add-user-modal/types";
import type { OrganizationRole } from "gql/graphql";
import { useDailyAlert, usePolicies } from "hooks";
import { Logger } from "utils/logger";
import { msgSuccess } from "utils/notifications";

const PADDING_B = 1.25;

const OrganizationMembers: FC<IOrganizationMembers> = ({
  organizationId,
}): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const useAlert = useDailyAlert("org-members", organizationId);
  const addModalRef = useModal("addStakeholderModal");

  const {
    data: stakeholdersList,
    loading: loadingStakeholders,
    refetch: refetchStakeholders,
  } = useOrganizationStakeholdersQuery(organizationId);

  const { orgPolicies, loadingPolicies } = usePolicies(organizationId);

  const [grantStakeholderAccess] = useMutation(ADD_STAKEHOLDER_MUTATION, {
    onCompleted: (mtResult): void => {
      if (mtResult.grantStakeholderOrganizationAccess.success) {
        refetchStakeholders().catch((): void => {
          Logger.error("An error occurred adding stakeholder");
        });
        const { email } =
          mtResult.grantStakeholderOrganizationAccess.grantedStakeholder ?? {};
        msgSuccess(
          `${t("searchFindings.tabUsers.success")} ${email}`,
          t("organization.tabs.users.successTitle"),
        );
      }
    },
    onError: (grantError): void => {
      handleGrantError(grantError);
    },
  });

  const handleSubmit = useCallback(
    async (values: IStakeholderFormValues): Promise<void> => {
      addModalRef.close();
      await grantStakeholderAccess({
        variables: {
          email: values.email,
          organizationId,
          role: values.role as OrganizationRole,
        },
      });
    },
    [addModalRef, grantStakeholderAccess, organizationId],
  );

  if (
    stakeholdersList.length <= 0 &&
    !loadingStakeholders &&
    !loadingPolicies
  ) {
    return (
      <Fragment>
        <SectionHeader
          header={t("organization.tabs.users.emptyComponent.header")}
        />
        <EmptyState
          confirmButton={{
            onClick: addModalRef.open,
            text: t("organization.tabs.users.emptyComponent.addButton.text"),
          }}
          description={t("organization.tabs.users.emptyComponent.description")}
          imageSrc={"integrates/resources/memberIcon"}
          title={t("organization.tabs.users.emptyComponent.title")}
        />
        <AddMembersModal
          action={"add"}
          allMembers={stakeholdersList}
          close={addModalRef.close}
          isOpen={addModalRef.isOpen}
          onSubmit={handleSubmit}
          organizationId={organizationId}
          selectedMembers={[]}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SectionHeader header={t("organization.members.title")} />
      <Container
        bgColor={theme.palette.gray[50]}
        height={"100hv"}
        id={"users"}
        padding={[1, PADDING_B, 1, PADDING_B]}
        px={1.25}
        py={1.25}
      >
        <Alert closable={true} mb={1} show={useAlert} variant={"info"}>
          <Text color={theme.palette.info[700]} fontWeight={"bold"} size={"md"}>
            {t("organization.members.policiesAlert", {
              inactivityPeriod: orgPolicies?.organization.inactivityPeriod ?? 0,
            })}
          </Text>
        </Alert>
        <MembersTable
          loading={loadingStakeholders}
          organizationId={organizationId}
          refetchStakeholders={refetchStakeholders}
          stakeholdersList={stakeholdersList}
        />
      </Container>
    </Fragment>
  );
};

export { OrganizationMembers };
